/**
 * FlowHub - useClientSubscriptions Hook
 * TanStack Query hooks for managing client subscriptions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabase';
import { logger } from '../../../shared/lib/logger';

/**
 * Hook to fetch client's subscriptions with product details
 */
export function useClientSubscriptions(userId?: string) {
  return useQuery({
    queryKey: ['client-subscriptions', userId],
    enabled: !!userId,
    retry: 1,
    staleTime: 30000,
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('client_subscriptions')
        .select(`
          *,
          product:products(
            id,
            name,
            description,
            monthly_price,
            setup_fee,
            trial_days,
            schema,
            n8n_instance:n8n_instances(
              id,
              name,
              api_url
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading client subscriptions', error);
        throw error;
      }

      const subscriptions = data || [];
      if (!subscriptions.length) {
        return [];
      }

      let cancelabilityMap: Record<string, any> = {};
      let nextOpenDueMap: Record<string, string> = {};

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.access_token) {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-cancel-eligibility`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ subscription_ids: subscriptions.map((sub: any) => sub.id) }),
            }
          );

          if (response.ok) {
            const payload = await response.json();
            cancelabilityMap = (payload.subscriptions || []).reduce(
              (acc: Record<string, any>, item: any) => {
                acc[item.subscription_id] = item;
                return acc;
              },
              {}
            );
          } else {
            const responseError = await response.json().catch(() => ({}));
            logger.warn('Could not load cancellation eligibility', { error: responseError });
          }
        }
      } catch (eligibilityError) {
        logger.warn('Eligibility lookup failed', { error: eligibilityError });
      }

      try {
        const today = new Date().toISOString().split('T')[0];
        const { data: openPayments, error: openPaymentsError } = await supabase
          .from('payments')
          .select('subscription_id, due_date, status')
          .eq('user_id', userId)
          .in('status', ['pending', 'awaiting_payment'])
          .gte('due_date', today)
          .order('due_date', { ascending: true });

        if (openPaymentsError) {
          logger.warn('Could not load open payments', { error: openPaymentsError });
        } else if (openPayments?.length) {
          nextOpenDueMap = openPayments.reduce((acc: Record<string, string>, payment: any) => {
            if (!payment?.subscription_id || !payment?.due_date) {
              return acc;
            }
            if (!acc[payment.subscription_id]) {
              acc[payment.subscription_id] = payment.due_date;
            }
            return acc;
          }, {});
        }
      } catch (openPaymentError) {
        logger.warn('Open payments lookup failed', { error: openPaymentError });
      }

      return subscriptions.map((subscription: any) => ({
        ...subscription,
        cancelability: cancelabilityMap[subscription.id],
        next_open_due_date: nextOpenDueMap[subscription.id],
      }));
    },
  });
}

/**
 * Hook to cancel a subscription (calls Edge Function)
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      subscriptionId, 
      cancelWorkflow = true 
    }: { 
      subscriptionId: string; 
      cancelWorkflow?: boolean;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Não autenticado');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            subscription_id: subscriptionId,
            cancel_workflow: cancelWorkflow,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const err = new Error(error.error || 'Erro ao cancelar assinatura');
        (err as any).code = error.code;
        (err as any).details = error;
        throw err;
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details'] });
    },
  });
}

/**
 * Hook to sync payment status from Asaas API
 */
export function useSyncPaymentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      subscriptionId, 
      syncAll = false 
    }: { 
      subscriptionId?: string; 
      syncAll?: boolean;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Não autenticado');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-payment-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            subscription_id: subscriptionId,
            sync_all: syncAll,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao sincronizar status de pagamento');
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details'] });
    },
  });
}

/**
 * Hook to sync all payments from Asaas API
 */
export function useSyncAsaasPayments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Não autenticado');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-asaas-payments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao sincronizar pagamentos');
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details'] });
    },
  });
}

/**
 * Hook to get subscription details including payment history
 */
export function useSubscriptionDetails(subscriptionId?: string) {
  return useQuery({
    queryKey: ['subscription-details', subscriptionId],
    enabled: !!subscriptionId,
    queryFn: async () => {
      if (!subscriptionId) return null;

      const { data: subscription, error: subError } = await supabase
        .from('client_subscriptions')
        .select(`
          *,
          product:products(
            id,
            name,
            description,
            monthly_price,
            setup_fee,
            trial_days,
            schema
          )
        `)
        .eq('id', subscriptionId)
        .single();

      if (subError) throw subError;

      // Get payment history
      const { data: payments, error: payError } = await supabase
        .from('payments')
        .select('*')
        .eq('subscription_id', subscriptionId)
        .order('created_at', { ascending: false });

      if (payError) throw payError;

      return {
        subscription,
        payments: payments || [],
      };
    },
  });
}
